import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { EditorToolbar } from './EditorToolbar';
import { ImageEditorCanvas } from './ImageEditorCanvas';
import {
  applyCropToCanvas,
  applyMaskToCanvas,
  canvasToFile,
  createMaskFromRegions,
  cropToContent,
  loadImage,
} from './imageExportUtils';
import { DEFAULT_NUM_SEGMENTS, DEFAULT_SENSITIVITY } from './SegmentTool';
import { CropRect, EditorTool } from './types';
import { useCrop } from './useCrop';
import { EditorCanvasBounds, fitWithinBounds } from './useEditorCanvasBounds';
import { UseImageEditorReturn } from './useImageEditor';
import { useSegmentation } from './useSegmentation';

export const useImageEditorActions = (editor: UseImageEditorReturn, initialCropRect?: CropRect) => {
  const { currentState, setActiveTool, setCropRect, setSegmentation, toggleRegionSelection, setEditedFile } = editor;
  const { detectCrop } = useCrop();
  const [numberSegments, setNumberSegments] = useState(DEFAULT_NUM_SEGMENTS);
  const [sensitivity, setSensitivity] = useState(DEFAULT_SENSITIVITY);

  const isSegmentMode = currentState?.activeTool === 'segment';
  const { segmentation, isSegmenting } = useSegmentation({
    file: isSegmentMode ? currentState?.originalFile : undefined,
    numberSegments,
    sensitivity,
  });

  useEffect(() => {
    if (segmentation) {
      setSegmentation(segmentation);
    }
  }, [segmentation]);

  const applyCrop = async (cropRect: CropRect) => {
    if (!currentState) return;
    const img = await loadImage(currentState.originalFile);
    const canvas = applyCropToCanvas(img, cropRect);
    const file = await canvasToFile(canvas, currentState.originalFile.name);
    setEditedFile(file);
  };

  const detectAndApplyCrop = async (file: File) => {
    const img = await loadImage(file);
    const rect = await detectCrop(img);
    if (!rect) return;
    setCropRect(rect);
    setEditedFile(await canvasToFile(applyCropToCanvas(img, rect), file.name));
  };

  const autoDetectCrop = (file: File) => {
    void detectAndApplyCrop(file);
  };

  const handleRegionToggle = async (regionId: number) => {
    if (!currentState?.segmentation) return;

    const nextSelected = new Set(currentState.selectedRegionIds);
    if (nextSelected.has(regionId)) {
      nextSelected.delete(regionId);
    } else {
      nextSelected.add(regionId);
    }

    toggleRegionSelection(regionId);

    if (nextSelected.size === 0) {
      setEditedFile(undefined);
      return;
    }

    const img = await loadImage(currentState.originalFile);
    const mask = createMaskFromRegions(currentState.segmentation, nextSelected, img.naturalWidth, img.naturalHeight);
    const masked = applyMaskToCanvas(img, mask);
    const canvas = cropToContent(masked);
    const file = await canvasToFile(canvas, currentState.originalFile.name);
    setEditedFile(file);
  };

  const handleToolChange = (tool: EditorTool) => {
    setActiveTool(tool);
    if (tool !== 'smartCrop' || !currentState) return;
    if (initialCropRect) {
      setCropRect(initialCropRect);
      void applyCrop(initialCropRect);
    } else {
      autoDetectCrop(currentState.originalFile);
    }
  };

  return {
    isSegmenting,
    numberSegments,
    sensitivity,
    setNumberSegments,
    setSensitivity,
    applyCrop,
    autoDetectCrop,
    handleRegionToggle,
    handleToolChange,
  };
};

export type ImageEditorActions = ReturnType<typeof useImageEditorActions>;

const useNaturalSize = (file: File | undefined) => {
  const [size, setSize] = useState<{ width: number; height: number }>();
  useEffect(() => {
    if (!file) return;
    void loadImage(file).then(img => setSize({ width: img.naturalWidth, height: img.naturalHeight }));
  }, [file]);
  return size;
};

type SearchPreviewProperties = {
  file: File;
  originalFile: File;
  bounds: EditorCanvasBounds;
  label: string;
};

const SearchPreview = ({ file, originalFile, bounds, label }: SearchPreviewProperties) => {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);
  const naturalSize = useNaturalSize(originalFile);
  const containerSize = naturalSize
    ? fitWithinBounds(naturalSize, bounds)
    : { width: bounds.maxWidth, height: bounds.maxHeight };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        width: containerSize.width,
      }}
    >
      <Box
        sx={{
          width: containerSize.width,
          height: containerSize.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <img
          src={previewUrl}
          alt={label}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
    </Box>
  );
};

type ImageEditorWorkspaceProperties = {
  editor: UseImageEditorReturn;
  actions: ImageEditorActions;
  bounds: EditorCanvasBounds;
  previewLabel?: string;
};

export const ImageEditorWorkspace = ({
  editor,
  actions,
  bounds,
  previewLabel = 'Search preview',
}: ImageEditorWorkspaceProperties) => {
  const { currentState, setCropRect, setLassoPoints, setEditedFile } = editor;
  if (!currentState) return;
  const previewFile = currentState.editedFile ?? currentState.originalFile;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <EditorToolbar
          activeTool={currentState.activeTool}
          onToolChange={actions.handleToolChange}
          isSegmenting={actions.isSegmenting}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', justifyContent: 'center' }}>
        <Box sx={{ flexShrink: 0 }}>
          <ImageEditorCanvas
            file={currentState.originalFile}
            activeTool={currentState.activeTool}
            cropRect={currentState.cropRect}
            lassoPoints={currentState.lassoPoints}
            segmentation={currentState.segmentation}
            selectedRegionIds={currentState.selectedRegionIds}
            onCropChange={setCropRect}
            onCropEnd={rect => {
              void actions.applyCrop(rect);
            }}
            onLassoPointsChange={setLassoPoints}
            onLassoExport={setEditedFile}
            onRegionClick={regionId => {
              void actions.handleRegionToggle(regionId);
            }}
            onToolChange={actions.handleToolChange}
            isSegmenting={actions.isSegmenting}
            numberSegments={actions.numberSegments}
            sensitivity={actions.sensitivity}
            onNumberSegmentsChange={actions.setNumberSegments}
            onSensitivityChange={actions.setSensitivity}
            maxWidth={bounds.maxWidth}
            maxHeight={bounds.maxHeight}
          />
        </Box>
        <SearchPreview
          file={previewFile}
          originalFile={currentState.originalFile}
          bounds={bounds}
          label={previewLabel}
        />
      </Box>
    </>
  );
};
